import { getOrderAppOrdersOrderIdGetOptions } from '@/client/@tanstack/react-query.gen'
import { Button } from '@/components/ui/button'
import { useAnalytics } from '@/hooks/use-analytics'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { OrderDetailPanel } from './-components/order-detail-panel'

export const Route = createFileRoute('/_authed/_user/orders/$orderId')({
  beforeLoad: async ({ params, context }) => {
    if (!context.auth.canUseOrderInbox) {
      throw redirect({ to: '/' })
    }
    // Pre-fetch so the breadcrumb can show the PO number. The page's
    // own useQuery hits the same cache key and renders instantly.
    // Failures (404, invalid id) fall through to a generic title.
    try {
      const order = await context.queryClient.ensureQueryData(
        getOrderAppOrdersOrderIdGetOptions({ path: { order_id: params.orderId } }),
      )
      return {
        getTitle: () => order.poNumber ?? 'Order',
      }
    }
    catch {
      return {
        getTitle: () => 'Order',
      }
    }
  },
  component: OrderDetailPage,
})

// Lightweight UUID v4-ish check — server is the source of truth for "real
// UUID", but rejecting obvious garbage in the client avoids a 422 round-trip
// and lets us render the not-found state immediately for typoed URLs.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function OrderDetailPage() {
  const { orderId } = Route.useParams()
  const isValidId = UUID_PATTERN.test(orderId)
  const { capture } = useAnalytics()
  const viewedRef = useRef(false)

  const { data, isLoading, isError, error, refetch } = useQuery({
    ...getOrderAppOrdersOrderIdGetOptions({ path: { order_id: orderId } }),
    staleTime: 30_000,
    enabled: isValidId,
  })

  const status
    = error && typeof error === 'object' && 'status' in error
      ? (error as { status: number }).status
      : undefined
  const isNotFound = !isValidId || status === 404

  useEffect(() => {
    if (viewedRef.current || isNotFound || isError || isLoading || !data)
      return
    viewedRef.current = true
    capture('orders_order_detail_viewed', {
      order_id: orderId,
      status: data.status,
      surface: 'order_detail_page',
    })
  }, [capture, data, isError, isLoading, isNotFound, orderId])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 py-4 lg:px-6 md:py-6">
      <Link
        to="/orders"
        className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Orders
      </Link>
      {isNotFound && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border bg-muted/30 p-10 text-center">
          <AlertTriangle className="h-8 w-8 text-muted-foreground" />
          <div>
            <h2 className="text-base font-medium">Order not found</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              The order you&rsquo;re looking for doesn&rsquo;t exist or you don&rsquo;t have access to it.
            </p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/orders">Back to Orders</Link>
          </Button>
        </div>
      )}

      {!isNotFound && isError && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-destructive/30 bg-destructive-soft p-10 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <div>
            <h2 className="text-base font-medium">Failed to load order</h2>
            <p className="mt-1 text-sm text-muted-foreground">Try again in a moment.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void refetch()
            }}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      )}

      {!isNotFound && !isError && (
        isLoading ? (
          <div className="flex items-center justify-center gap-2 rounded-md border p-10 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading order details...
          </div>
        ) : (
          <OrderDetailPanel orderId={orderId} actionSurface="order_detail_page" />
        )
      )}
    </div>
  )
}
